import { useCallback, useId } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Input, FormField } from '@amzn/gsce-shared-react-lib/components';

import { RichTextEditorField } from '../RichTextEditorField';

/** The Helios task shape these fields edit (name, description, optional days). */
export interface TaskDetailsValue {
  readonly taskName: string;
  readonly taskDescription: string;
  readonly taskDurationDays?: number | undefined;
}

/** Visible labels/placeholders — supplied by the caller so this stays
 *  i18n-bundle-agnostic and reusable across features. */
export interface TaskDetailsFieldsLabels {
  readonly nameLabel: string;
  readonly namePlaceholder: string;
  readonly descriptionLabel: string;
  readonly descriptionPlaceholder: string;
  readonly durationLabel: string;
  readonly durationPlaceholder: string;
}

/** Per-field inline-error MESSAGES. When a field's message is set, FormField
 *  renders it below the control (and wires `aria-describedby`/`aria-invalid`).
 *  Omitted/undefined = no error. Caller supplies the localized string so this
 *  component stays i18n-bundle-agnostic. */
export interface TaskDetailsFieldsErrors {
  readonly name?: string;
  readonly description?: string;
  readonly duration?: string;
}

/** Module-level default so an omitted `errors` prop is reference-stable. */
const NO_ERRORS: TaskDetailsFieldsErrors = {};

/** The ARIA attribute pointing a control at its error text (hoisted — the
 *  standards scanner flags the repeated literal across the name + duration
 *  inputs). */
const ARIA_DESCRIBEDBY = 'aria-describedby';

export interface TaskDetailsFieldsProps {
  /** Current task value. */
  readonly value: TaskDetailsValue;
  /** Emits the full next task value on any field edit. */
  readonly onChange: (next: TaskDetailsValue) => void;
  readonly labels: TaskDetailsFieldsLabels;
  /** Base tracking id; each field appends its own suffix (`.task-name` etc.)
   *  for the shared Input's mandatory change tracking. */
  readonly trackingIdPrefix: string;
  /**
   * Whether the task fields are required (visible required marker). Callers
   * that only render this once a task has been added pass `true` (name,
   * description, and duration are all required for that task); a surface where
   * the whole task block is optional (e.g. the parent-question Audit Criteria
   * step) passes `false`. Defaults to `true`.
   */
  readonly isRequired?: boolean;
  /** Per-field inline-error messages; defaults to none. */
  readonly errors?: TaskDetailsFieldsErrors;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * The editable Task fields — Task name, Duration (days), Task description
 * (rich text) — shared by the audit-criteria step and each sub-question row.
 * Fully controlled: emits the whole next `TaskDetailsValue` on every edit;
 * the caller owns whether a task exists at all (add/remove) and where the
 * value is stored.
 *
 * Labels, required markers, and inline error text are rendered by the shared
 * {@link FormField}; name/duration use the shared tracked {@link Input}.
 * Duration is digit-filtered and cleared to `undefined` when emptied (so it
 * round-trips under `exactOptionalPropertyTypes`).
 */
export function TaskDetailsFields({
  value,
  onChange,
  labels,
  trackingIdPrefix,
  isRequired = true,
  errors = NO_ERRORS,
  disabled = false,
  dataTestId,
}: TaskDetailsFieldsProps): JSX.Element {
  const nameIds = useFieldIds();
  const durationIds = useFieldIds();
  const descriptionIds = useFieldIds();

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, taskName: e.target.value }),
    [value, onChange],
  );

  const handleDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (!/^\d*$/.test(raw)) return; // reject non-digits before state
      // Clearing drops the key (rather than setting undefined) so it
      // round-trips under exactOptionalPropertyTypes.
      onChange(
        raw === ''
          ? { taskName: value.taskName, taskDescription: value.taskDescription }
          : { ...value, taskDurationDays: Number(raw) },
      );
    },
    [value, onChange],
  );

  const handleDescriptionChange = useCallback(
    (html: string) => onChange({ ...value, taskDescription: html }),
    [value, onChange],
  );

  const durationValue =
    value.taskDurationDays !== undefined ? String(value.taskDurationDays) : '';

  return (
    <Col gridGap="dimensions.spacing.300" width="100%">
      <Row gridGap="dimensions.spacing.300" flexWrap="wrap">
        <Col gridGap="dimensions.spacing.100" flex={1} minWidth={0}>
          <FormField
            label={labels.nameLabel}
            required={isRequired}
            labelId={nameIds.labelId}
            htmlFor={nameIds.controlId}
            {...(errors.name !== undefined
              ? { error: errors.name, errorId: nameIds.errorId }
              : {})}
          >
            <Input
              id={nameIds.controlId}
              trackingId={`${trackingIdPrefix}.task-name`}
              value={value.taskName}
              onChange={handleNameChange}
              placeholder={labels.namePlaceholder}
              disabled={disabled}
              labelId={nameIds.labelId}
              error={errors.name !== undefined}
              aria-invalid={errors.name !== undefined}
              {...(errors.name !== undefined
                ? { [ARIA_DESCRIBEDBY]: nameIds.errorId }
                : {})}
              {...(dataTestId ? { dataTestId: `${dataTestId}-taskName` } : {})}
            />
          </FormField>
        </Col>
        <Col gridGap="dimensions.spacing.100">
          <FormField
            label={labels.durationLabel}
            required={isRequired}
            labelId={durationIds.labelId}
            htmlFor={durationIds.controlId}
            {...(errors.duration !== undefined
              ? { error: errors.duration, errorId: durationIds.errorId }
              : {})}
          >
            <Input
              id={durationIds.controlId}
              trackingId={`${trackingIdPrefix}.task-duration`}
              value={durationValue}
              onChange={handleDurationChange}
              placeholder={labels.durationPlaceholder}
              disabled={disabled}
              labelId={durationIds.labelId}
              error={errors.duration !== undefined}
              aria-invalid={errors.duration !== undefined}
              {...(errors.duration !== undefined
                ? { [ARIA_DESCRIBEDBY]: durationIds.errorId }
                : {})}
              {...(dataTestId ? { dataTestId: `${dataTestId}-taskDuration` } : {})}
            />
          </FormField>
        </Col>
      </Row>

      {/* Description is a RichTextEditor (TipTap), which has no FormField-aware
       *  control ids — FormField still owns the label + inline error text. */}
      <FormField
        label={labels.descriptionLabel}
        required={isRequired}
        labelId={descriptionIds.labelId}
        {...(errors.description !== undefined
          ? { error: errors.description, errorId: descriptionIds.errorId }
          : {})}
      >
        <RichTextEditorField
          value={value.taskDescription}
          onChange={handleDescriptionChange}
          placeholder={labels.descriptionPlaceholder}
          disabled={disabled}
          ariaLabel={labels.descriptionLabel}
          invalid={errors.description !== undefined}
          trackingId={`${trackingIdPrefix}.task-description`}
          {...(errors.description !== undefined
            ? { ariaDescribedBy: descriptionIds.errorId }
            : {})}
          {...(dataTestId !== undefined
            ? { dataTestId: `${dataTestId}-taskDescription` }
            : {})}
        />
      </FormField>
    </Col>
  );
}

/** Stable label/control/error ids for one FormField-wrapped control. */
function useFieldIds(): {
  readonly labelId: string;
  readonly controlId: string;
  readonly errorId: string;
} {
  const base = useId();
  return {
    labelId: `${base}-label`,
    controlId: `${base}-control`,
    errorId: `${base}-error`,
  };
}
